/**
 * HeartRateCard Component
 * Dedicated real-time heart rate display for run screen
 * Shows: Current BPM, Zone, Connection Status, Avg/Max
 */

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BPMZone } from '@/services/bpm-service';
import { Heart, Bluetooth, BluetoothOff, Wifi } from 'lucide-react';
import { motion } from 'framer-motion';

interface HeartRateCardProps {
  currentBPM: number;
  avgBPM: number;
  maxBPM: number;
  zone: BPMZone | null;
  isMonitoring: boolean;
  deviceConnected: boolean;
  deviceName?: string;
  source?: 'ble' | 'healthkit' | 'googlefit' | 'none';
  onConnect?: () => void;
}

export function HeartRateCard({ 
  currentBPM, 
  avgBPM, 
  maxBPM, 
  zone, 
  isMonitoring,
  deviceConnected,
  deviceName,
  source = 'none',
  onConnect
}: HeartRateCardProps) {
  
  // Determine connection icon
  const ConnectionIcon = deviceConnected 
    ? (source === 'ble' ? Bluetooth : Wifi)
    : BluetoothOff;

  // Determine if showing real data or placeholder
  const hasData = currentBPM > 0 && isMonitoring;

  return (
    <Card className="relative overflow-hidden">
      {/* Background gradient based on zone */}
      <div 
        className="absolute inset-0 opacity-10 transition-all duration-500"
        style={{
          background: zone ? `linear-gradient(135deg, ${zone.color}00 0%, ${zone.color}40 100%)` : 'transparent'
        }}
      />

      <div className="relative p-4">
        {/* Header: Icon + Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <motion.div
              animate={{
                scale: hasData ? [1, 1.1, 1] : 1
              }}
              transition={{
                repeat: hasData ? Infinity : 0,
                duration: 60 / (currentBPM || 60), // Beat with actual heart rate
                ease: "easeInOut"
              }}
            >
              <Heart 
                className="w-5 h-5" 
                style={{ 
                  color: zone?.color || 'hsl(var(--destructive))',
                  fill: hasData ? zone?.color || 'hsl(var(--destructive))' : 'transparent'
                }} 
              />
            </motion.div>
            <h3 className="font-semibold text-sm">Frequência Cardíaca</h3>
          </div>

          {/* Connection Status Badge */}
          <Badge 
            variant={deviceConnected ? "default" : "outline"}
            className="text-xs gap-1"
          >
            <ConnectionIcon className="w-3 h-3" />
            {deviceConnected 
              ? (deviceName || (source === 'ble' ? 'BLE' : source === 'healthkit' ? 'HealthKit' : 'Sensor'))
              : 'Desconectado'}
          </Badge>
        </div>

        {/* Current BPM - Large Display */}
        <div className="text-center py-2">
          <motion.div 
            className="text-5xl font-bold tabular-nums"
            style={{ color: zone?.color || 'hsl(var(--foreground))' }}
            animate={{
              opacity: hasData ? 1 : 0.5
            }}
            transition={{ duration: 0.3 }}
          >
            {hasData ? currentBPM : '--'}
          </motion.div>
          <div className="text-xs text-muted-foreground mt-1">BPM</div>

          {/* Zone Badge */}
          {zone && hasData && (
            <Badge
              className="mt-2 font-medium"
              style={{ 
                backgroundColor: zone.color,
                color: 'white'
              }}
            >
              {zone.name}
            </Badge>
          )}
        </div>

        {/* Stats Row: Average & Maximum */}
        {isMonitoring && hasData && (
          <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-border/50">
            <div className="text-center">
              <div className="text-xl font-semibold tabular-nums">{avgBPM || '--'}</div>
              <div className="text-xs text-muted-foreground">Média</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-semibold tabular-nums">{maxBPM || '--'}</div>
              <div className="text-xs text-muted-foreground">Máximo</div>
            </div>
          </div>
        )}

        {/* Zone Guide */}
        {zone && hasData && (
          <div className="pt-3 mt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground text-center">
              Zona: {zone.min.toFixed(0)} - {zone.max.toFixed(0)} BPM
            </p>
          </div>
        )}

        {/* No Data / Connect Prompt */}
        {!hasData && onConnect && (
          <div className="text-center pt-2 pb-1">
            <button
              onClick={onConnect}
              className="text-xs text-primary hover:underline font-medium"
            >
              {deviceConnected ? 'Aguardando dados...' : 'Conectar Monitor Cardíaco'}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
